import React, { useState, useEffect } from 'react';
import { Home, Rocket, GraduationCap, Tag, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { CATEGORY_LABELS, PUBLIC_CATEGORY_FILTERS } from '../config';

// ── Refonte 2026-06-01 (v2) : SIDEBAR UNIQUE, listes verticales ──────────────
// • Chaque valeur sur SA ligne : libellé à gauche, compteur aligné à droite (lisible).
// • Filtres COHÉRENTS par catégorie : CPU/RAM seulement pour les PC (portable/fixe/
//   serveur), taille d'écran seulement pour portable (diagonale) et écran (moniteur).
//   Plus jamais de « taille d'écran » sur un PC fixe.
// • Compteurs dynamiques (exclude-own côté backend), options à 0 grisées.

const PROFILES = [
  { key: 'quotidien', label: 'Quotidien', icon: Home,
    preset: { category: '', ramMin: '8', cpu: '', screenMin: '', screenMax: '', condition: '', minPrice: '', maxPrice: '' } },
  { key: 'puissant', label: 'Puissant', icon: Rocket,
    preset: { category: '', ramMin: '16', cpu: '', screenMin: '', screenMax: '', condition: '', minPrice: '', maxPrice: '' } },
  { key: 'etudiant', label: 'Nomade', icon: GraduationCap,
    preset: { category: 'portable', ramMin: '8', cpu: '', screenMin: '13', screenMax: '15', condition: '', minPrice: '', maxPrice: '' } },
  { key: 'affaires', label: 'Bonnes affaires', icon: Tag,
    preset: { category: '', ramMin: '', cpu: '', screenMin: '', screenMax: '', condition: 'reconditionne', minPrice: '', maxPrice: '' } },
];

const CPU_USAGE = {
  i3: 'essentiel', 'Celeron/Pentium': 'essentiel', i5: 'polyvalent', 'Ryzen 5': 'polyvalent',
  i7: 'performant', 'Ryzen 7': 'performant', i9: 'très performant', 'Ryzen 9': 'très performant',
  'Core Ultra': 'dernière gén.', 'Ryzen 3': 'essentiel',
};

const PRINTER_TECH_LABELS = { laser: 'Laser', jet: "Jet d'encre" };
const PRINTER_COLOR_LABELS = { couleur: 'Couleur', monochrome: 'Monochrome (N&B)' };
const PRINTER_MFP_LABELS = { multifonction: 'Multifonction', simple: 'Imprimante simple' };

const SCREEN_BUCKETS_PORTABLE = [
  { label: '13 pouces', min: '13', max: '13.9' }, { label: '14 pouces', min: '14', max: '14.9' },
  { label: '15 – 16 pouces', min: '15', max: '16.5' }, { label: '17 pouces et +', min: '17', max: '' },
];
const SCREEN_BUCKETS_ECRAN = [
  { label: '24 pouces', min: '23', max: '24.9' }, { label: '27 pouces', min: '25', max: '27.9' },
  { label: '32 pouces et +', min: '28', max: '' },
];

// Filtres techniques pertinents selon la catégorie sélectionnée (cohérence métier).
const showCpuRamFor = (cat) => ['portable', 'pc_fixe', 'serveur'].includes(cat);
const screenKindFor = (cat) => (cat === 'portable' ? 'laptop' : cat === 'ecran' ? 'monitor' : null);

const countMap = (arr) => Object.fromEntries((arr || []).map((o) => [String(o.value), o.count]));

function Group({ title, children }) {
  return (
    <div className="py-3 border-b border-slate-200 last:border-0">
      <h4 className="font-accent text-[11px] uppercase tracking-widest text-slate-500 mb-1.5">{title}</h4>
      {children}
    </div>
  );
}

// Ligne de filtre : libellé à gauche, compteur aligné à droite (même ligne).
// count===null → pas de compteur (ex: "Tous"). count===0 → grisé, non cliquable.
function Row({ active, count, onClick, children }) {
  const disabled = count === 0;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-sm text-sm border text-left transition-colors ${
        active
          ? 'bg-secondary/10 text-secondary border-secondary/40 font-medium'
          : disabled
            ? 'text-slate-300 border-transparent cursor-not-allowed'
            : 'text-slate-600 border-transparent hover:bg-slate-50 hover:border-slate-200'
      }`}
    >
      <span className="truncate">{children}</span>
      {count != null && (
        <span className={`shrink-0 text-[11px] tabular-nums ${active ? 'text-secondary/80' : 'text-slate-400'}`}>{count}</span>
      )}
    </button>
  );
}

// Slider de prix double (min↔max) sans dépendance externe : deux <input range>
// superposés sur la même piste. Les bornes viennent des prix RÉELS du périmètre
// filtré (facets.price_min/max, exclude-own côté backend). On garde l'état en local
// pendant le glissement (fluide) et on ne pousse vers les filtres qu'au relâchement
// pour ne pas refiltrer le catalogue à chaque tick.
function PriceSlider({ min, max, minPrice, maxPrice, onChange }) {
  const floor = Number.isFinite(min) ? Math.floor(min) : 0;
  const ceil = Number.isFinite(max) ? Math.ceil(max) : 0;
  const usable = ceil > floor;

  // Valeurs courantes = sélection utilisateur (si présente) bornée, sinon les bornes.
  const selLo = minPrice !== '' && minPrice != null ? Math.max(floor, Math.min(ceil, Number(minPrice))) : floor;
  const selHi = maxPrice !== '' && maxPrice != null ? Math.max(floor, Math.min(ceil, Number(maxPrice))) : ceil;

  const [lo, setLo] = useState(selLo);
  const [hi, setHi] = useState(selHi);

  // Resynchronise quand les bornes (changement de périmètre) ou la sélection
  // externe (reset global, URL) évoluent.
  useEffect(() => { setLo(selLo); setHi(selHi); }, [selLo, selHi]);

  if (!usable) {
    return <p className="text-sm text-slate-400 px-1 py-1">Aucun prix à filtrer.</p>;
  }

  const hasSelection = lo > floor || hi < ceil;

  // Pousse la sélection dans les filtres : une borne égale au plancher/plafond est
  // considérée « pas de contrainte » → vidée (undefined) pour ne pas afficher un
  // filtre actif inutile et garder l'URL propre.
  const commit = (nlo, nhi) => {
    onChange({
      minPrice: nlo > floor ? String(nlo) : '',
      maxPrice: nhi < ceil ? String(nhi) : '',
    });
  };

  const onLo = (e) => { const v = Math.min(Number(e.target.value), hi); setLo(v); };
  const onHi = (e) => { const v = Math.max(Number(e.target.value), lo); setHi(v); };
  const reset = () => { setLo(floor); setHi(ceil); onChange({ minPrice: '', maxPrice: '' }); };

  // % de la piste pour colorer la plage active entre les deux poignées.
  const pct = (v) => ((v - floor) / (ceil - floor)) * 100;

  return (
    <div className="px-1 pt-1" data-testid="price-slider">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-primary tabular-nums">
          {lo} € – {hi} €
        </span>
        {hasSelection && (
          <button
            type="button"
            onClick={reset}
            className="text-[11px] font-medium uppercase tracking-wider text-secondary hover:underline"
            data-testid="price-reset"
          >
            Réinitialiser
          </button>
        )}
      </div>

      <div className="price-range relative h-5">
        {/* Piste de fond + plage active */}
        <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1 rounded-full bg-slate-200" />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1 rounded-full bg-secondary"
          style={{ left: `${pct(lo)}%`, right: `${100 - pct(hi)}%` }}
        />
        <input
          type="range"
          min={floor}
          max={ceil}
          value={lo}
          onChange={onLo}
          onMouseUp={() => commit(lo, hi)}
          onTouchEnd={() => commit(lo, hi)}
          onKeyUp={() => commit(lo, hi)}
          className="price-range__input"
          aria-label="Prix minimum"
          data-testid="price-slider-min"
        />
        <input
          type="range"
          min={floor}
          max={ceil}
          value={hi}
          onChange={onHi}
          onMouseUp={() => commit(lo, hi)}
          onTouchEnd={() => commit(lo, hi)}
          onKeyUp={() => commit(lo, hi)}
          className="price-range__input"
          aria-label="Prix maximum"
          data-testid="price-slider-max"
        />
      </div>

      <div className="flex justify-between text-[11px] text-slate-400 tabular-nums mt-1">
        <span>{floor} €</span>
        <span>{ceil} €</span>
      </div>
    </div>
  );
}

export default function SidebarFilters({ filters, facets, profile, onProfile, onChange }) {
  const [openMobile, setOpenMobile] = useState(false);
  const set = (patch) => onChange(patch);

  const cat = filters.category;
  const showCpuRam = showCpuRamFor(cat);
  const screenKind = screenKindFor(cat);
  const isImprimante = cat === 'imprimante';
  const screenBuckets = screenKind === 'monitor' ? SCREEN_BUCKETS_ECRAN : SCREEN_BUCKETS_PORTABLE;
  const screenActive = (b) => filters.screenMin === b.min && (filters.screenMax || '') === (b.max || '');

  const condCounts = countMap(facets.conditions);
  const catCounts = countMap(facets.categories);
  const screenBucketCount = (b) => (facets.screen_values || []).reduce((n, sv) => {
    const ok = sv.value >= parseFloat(b.min) && (b.max === '' || sv.value <= parseFloat(b.max));
    return ok ? n + sv.count : n;
  }, 0);

  const activeCount = ['category', 'condition', 'minPrice', 'maxPrice', 'brand', 'cpu', 'ramMin', 'screenMin', 'printerTech', 'printerColor', 'printerMfp', 'screenRes']
    .filter((k) => filters[k]).length;

  const resetAll = () => { onProfile('', null); onChange({ category: '', condition: '', minPrice: '', maxPrice: '', brand: '', cpu: '', ramMin: '', screenMin: '', screenMax: '', printerTech: '', printerColor: '', printerMfp: '', screenRes: '' }); };

  return (
    <aside className="bg-white border border-slate-200 rounded-sm p-4 lg:sticky lg:top-[10rem] self-start mb-6 lg:mb-0" data-testid="sidebar-filters">
      <div className="flex items-center justify-between lg:mb-1">
        <span className="flex items-center gap-2 font-heading text-sm uppercase tracking-wider text-primary">
          <SlidersHorizontal className="w-4 h-4 text-secondary" /> Filtres
          {activeCount > 0 && <span className="text-[10px] bg-secondary text-white rounded-full px-1.5 py-0.5">{activeCount}</span>}
        </span>
        <button type="button" onClick={() => setOpenMobile((o) => !o)} className="lg:hidden text-xs font-medium uppercase tracking-wider text-secondary">
          {openMobile ? 'Masquer' : 'Afficher'}
        </button>
      </div>

      <div className={`${openMobile ? 'block' : 'hidden'} lg:block`}>
        {/* Presets rapides */}
        <Group title="Usage rapide">
          <div className="grid grid-cols-2 gap-1.5">
            {PROFILES.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => onProfile(p.key, p.preset)}
                className={`flex items-center gap-1.5 px-2 py-2 rounded-sm text-xs font-medium border transition-colors text-left ${
                  profile === p.key ? 'bg-secondary/10 text-secondary border-secondary/40' : 'bg-white text-slate-600 border-slate-200 hover:border-secondary'
                }`}
              >
                <p.icon className="w-3.5 h-3.5 shrink-0" /> <span className="leading-tight">{p.label}</span>
              </button>
            ))}
          </div>
        </Group>

        {/* État */}
        <Group title="État">
          <Row active={!filters.condition} count={null} onClick={() => set({ condition: '' })}>Tous</Row>
          <Row active={filters.condition === 'reconditionne'} count={condCounts.reconditionne ?? 0} onClick={() => set({ condition: filters.condition === 'reconditionne' ? '' : 'reconditionne' })}>Reconditionné</Row>
          <Row active={filters.condition === 'neuf'} count={condCounts.neuf ?? 0} onClick={() => set({ condition: filters.condition === 'neuf' ? '' : 'neuf' })}>Neuf</Row>
        </Group>

        {/* Catégorie */}
        <Group title="Catégorie">
          <Row active={!filters.category} count={null} onClick={() => set({ category: '' })}>Toutes les catégories</Row>
          {PUBLIC_CATEGORY_FILTERS.filter((slug) => (catCounts[slug] ?? 0) > 0 || filters.category === slug).map((slug) => (
            <Row key={slug} active={filters.category === slug} count={catCounts[slug] ?? 0} onClick={() => set({ category: filters.category === slug ? '' : slug })}>
              {CATEGORY_LABELS[slug] || slug}
            </Row>
          ))}
        </Group>

        {/* Budget — slider min↔max sur les prix réels du périmètre filtré */}
        <Group title="Budget">
          <PriceSlider
            min={facets.price_min}
            max={facets.price_max}
            minPrice={filters.minPrice}
            maxPrice={filters.maxPrice}
            onChange={set}
          />
        </Group>

        {/* Marque (liste défilable si nombreuse) */}
        {facets.brands?.length > 0 && (
          <Group title="Marque">
            <div className="max-h-56 overflow-y-auto -mr-1 pr-1">
              <Row active={!filters.brand} count={null} onClick={() => set({ brand: '' })}>Toutes les marques</Row>
              {facets.brands.map((b) => (
                <Row key={b.value} active={filters.brand === b.value} count={b.count} onClick={() => set({ brand: filters.brand === b.value ? '' : b.value })}>
                  {b.value}
                </Row>
              ))}
            </div>
          </Group>
        )}

        {/* Imprimante : technologie / couleur / type — catégorie imprimante, si données */}
        {isImprimante && facets.printer_tech?.length > 0 && (
          <Group title="Technologie">
            {facets.printer_tech.map((t) => (
              <Row key={t.value} active={filters.printerTech === t.value} count={t.count} onClick={() => set({ printerTech: filters.printerTech === t.value ? '' : t.value })}>
                {PRINTER_TECH_LABELS[t.value] || t.value}
              </Row>
            ))}
          </Group>
        )}
        {isImprimante && facets.printer_color?.length > 0 && (
          <Group title="Couleur">
            {facets.printer_color.map((c) => (
              <Row key={c.value} active={filters.printerColor === c.value} count={c.count} onClick={() => set({ printerColor: filters.printerColor === c.value ? '' : c.value })}>
                {PRINTER_COLOR_LABELS[c.value] || c.value}
              </Row>
            ))}
          </Group>
        )}
        {isImprimante && facets.printer_mfp?.length > 0 && (
          <Group title="Type">
            {facets.printer_mfp.map((m) => (
              <Row key={m.value} active={filters.printerMfp === m.value} count={m.count} onClick={() => set({ printerMfp: filters.printerMfp === m.value ? '' : m.value })}>
                {PRINTER_MFP_LABELS[m.value] || m.value}
              </Row>
            ))}
          </Group>
        )}

        {/* Processeur — PC uniquement */}
        {showCpuRam && facets.cpu_families?.length > 0 && (
          <Group title="Processeur">
            {facets.cpu_families.map((c) => (
              <Row key={c.value} active={filters.cpu === c.value} count={c.count} onClick={() => set({ cpu: filters.cpu === c.value ? '' : c.value })}>
                {c.value}{CPU_USAGE[c.value] ? <span className="text-slate-400 font-normal"> · {CPU_USAGE[c.value]}</span> : null}
              </Row>
            ))}
          </Group>
        )}

        {/* Mémoire — PC uniquement, paliers « ≥ X Go » */}
        {showCpuRam && facets.ram_options?.length > 0 && (
          <Group title="Mémoire (RAM)">
            {facets.ram_options.map((r) => (
              <Row key={r.value} active={filters.ramMin === String(r.value)} count={r.count} onClick={() => set({ ramMin: filters.ramMin === String(r.value) ? '' : String(r.value) })}>
                {r.value} Go et plus
              </Row>
            ))}
          </Group>
        )}

        {/* Taille écran — portable (diagonale) ou écran (moniteur) uniquement */}
        {screenKind && facets.screen_min != null && (
          <Group title={screenKind === 'monitor' ? "Taille de l'écran" : "Taille de l'écran (portable)"}>
            {screenBuckets.map((b) => (
              <Row key={b.label} active={screenActive(b)} count={screenBucketCount(b)} onClick={() => set(screenActive(b) ? { screenMin: '', screenMax: '' } : { screenMin: b.min, screenMax: b.max })}>
                {b.label}
              </Row>
            ))}
          </Group>
        )}

        {/* Résolution — catégorie écran, si données */}
        {cat === 'ecran' && facets.screen_resolutions?.length > 0 && (
          <Group title="Résolution">
            {facets.screen_resolutions.map((r) => (
              <Row key={r.value} active={filters.screenRes === r.value} count={r.count} onClick={() => set({ screenRes: filters.screenRes === r.value ? '' : r.value })}>
                {r.value}
              </Row>
            ))}
          </Group>
        )}

        <button
          type="button"
          onClick={resetAll}
          className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-medium uppercase tracking-wider text-slate-500 hover:text-secondary py-2"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Réinitialiser
        </button>
      </div>
    </aside>
  );
}
